/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from './apiService';
import { StudentInteractionData, StudentParticipation } from 'types/common';
import { datadogLogs } from '@datadog/browser-logs';

// Custom event for student participation updates
export const STUDENT_PARTICIPATION_UPDATE_EVENT = 'student-participation-update';

// Interface for the custom event detail
export interface StudentParticipationUpdateEvent {
  windowIndex: number;
  students: StudentParticipation[];
}

interface UseStudentInteractionResult {
  interactionData: StudentInteractionData | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  isMockData: boolean;
}

/**
 * Hook to fetch and manage student interaction data
 * @param classId - The ID of the class to fetch interaction data for
 * @param pollingInterval - Optional polling interval in milliseconds (default: 60000 - 1 minute)
 * @returns Object containing interaction data, loading state, error state, and refetch function
 */
const useStudentInteraction = (
  classId: number | string,
  pollingInterval = 10000
): UseStudentInteractionResult => {
  const [interactionData, setInteractionData] = useState<StudentInteractionData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [isMockData, setIsMockData] = useState<boolean>(false);
  
  // Use a ref to track if data is being fetched to prevent race conditions
  const isFetchingRef = useRef<boolean>(false);
  // Use a ref to store the previous data for comparison
  const prevDataRef = useRef<StudentInteractionData | null>(null);

  // Function to dispatch custom event for student participation updates
  const dispatchStudentParticipationUpdate = useCallback((windowIndex: number, students: StudentParticipation[]) => {
    const event = new CustomEvent<StudentParticipationUpdateEvent>(STUDENT_PARTICIPATION_UPDATE_EVENT, {
      detail: {
        windowIndex,
        students
      }
    });
    document.dispatchEvent(event);
  }, []);

  // Ref to track the last fetch time
  const lastFetchTimeRef = useRef<number>(0);
  
  // Function to fetch student interaction data
  const fetchInteractionData = useCallback(async () => {
    // Prevent multiple concurrent fetches
    if (isFetchingRef.current) {
      return;
    }
    
    if (!classId) {
      setIsLoading(false);
      return;
    }

    // Log the time between fetches for debugging
    const now = Date.now();
  
    lastFetchTimeRef.current = now;

    try {
      isFetchingRef.current = true;
      
      // Only show loading indicator on initial load
      if (!prevDataRef.current) {
        setIsLoading(true);
      }
      
      // Reset mock data flag before making the API call
      setIsMockData(false);
      
      const response = await api.getStudentInteraction(classId);
      
      if (response && response.data) {
        try {
          // Process dates to ensure they are Date objects
          let data = response.data;
          
          // Handle nested data structure if present
          if (data.data && data.data.data) {
            data = data.data.data;
          } else if (data.data) {
            data = data.data;
          }
          
          // Check if we have valid data from the API
          if (data.fifteenMinuteWindows && data.fifteenMinuteWindows.length > 0) {
            // Process real data
            const processedData: StudentInteractionData = {
              ...data,
              classId,
              classStartTime: new Date(data.classStartTime),
              currentWindow: data.currentWindow,
              fifteenMinuteWindows: data.fifteenMinuteWindows.map((window: any) => ({
                ...window,
                intervalStart: new Date(window.intervalStart),
                intervalEnd: new Date(window.intervalEnd)
              }))
            };
            
            // Check if we need to update the state or just dispatch events
            if (!prevDataRef.current) {
              // First load, set the data
              setInteractionData(processedData);
              prevDataRef.current = processedData;
            } else {
              // Check for changes and dispatch events
              const prevWindows = prevDataRef.current.fifteenMinuteWindows;
              const newWindows = processedData.fifteenMinuteWindows;
              
              // Only update the state when there are meaningful changes
              // This prevents unnecessary re-renders
              const shouldUpdateState = prevWindows.length !== newWindows.length || 
                  prevDataRef.current.currentWindow !== processedData.currentWindow;
              
              // Check for significant changes in student data
              if (!shouldUpdateState) {
                let hasSignificantChanges = false;
                
                // Only check the current window for significant changes to minimize processing
                const currentWindowIndex = processedData.currentWindow ? processedData.currentWindow - 1 : 0;
                if (currentWindowIndex >= 0 && currentWindowIndex < newWindows.length) {
                  const prevWindow = prevWindows[currentWindowIndex];
                  const newWindow = newWindows[currentWindowIndex];
                  
                  // If student count changed, that's a significant change
                  if (prevWindow.studentParticipation.length !== newWindow.studentParticipation.length) {
                    hasSignificantChanges = true;
                  } else {
                    // Check if any student's participation percentage changed significantly
                    for (let j = 0; j < newWindow.studentParticipation.length; j++) {
                      const newStudent = newWindow.studentParticipation[j];
                      const prevStudent = prevWindow.studentParticipation.find(
                        s => s.studentId === newStudent.studentId
                      );
                      
                      if (!prevStudent || 
                          Math.abs(prevStudent.participationPercentage - newStudent.participationPercentage) > 5) {
                        hasSignificantChanges = true;
                        break;
                      }
                    }
                  }
                }
                
                // Update state only if there are significant changes
                if (hasSignificantChanges) {
                  setInteractionData(processedData);
                }
              } else {
                // Update state if window count or current window changed
                setInteractionData(processedData);
              }
              
              // Process all windows for event dispatching
              for (let i = 0; i < newWindows.length; i++) {
                const prevWindow = prevWindows[i];
                const newWindow = newWindows[i];
                
                // Check if any student's participation percentage changed
                let hasStudentChanges = false;
                
                for (let j = 0; j < newWindow.studentParticipation.length; j++) {
                  const newStudent = newWindow.studentParticipation[j];
                  const prevStudent = prevWindow.studentParticipation.find(
                    s => s.studentId === newStudent.studentId
                  );
                  
                  if (!prevStudent || 
                      Math.abs(prevStudent.participationPercentage - newStudent.participationPercentage) > 0.5) {
                    hasStudentChanges = true;
                    break;
                  }
                }
                
                // If student data changed, dispatch an event
                if (hasStudentChanges) {
                  dispatchStudentParticipationUpdate(i, newWindow.studentParticipation);
                }
              }
              
              // Always update the ref with the latest data
              prevDataRef.current = processedData;
            }
            
            setIsMockData(false);
          } else {
            // No valid data from API, use mock data
            // const mockData = createMockInteractionData(classId);
            // setInteractionData(mockData);
            // prevDataRef.current = mockData;
            // setIsMockData(true);
          }
          setError(null);
        } catch (err) {
          // Error processing interaction data, create mock data as fallback
          // const mockData = createMockInteractionData(classId);
          // setInteractionData(mockData);
          // prevDataRef.current = mockData;
          // setIsMockData(true);
          // setError(null);
        }
      } else {
        // Create mock data as fallback
        // const mockData = createMockInteractionData(classId);
        // setInteractionData(mockData);
        // prevDataRef.current = mockData;
        // setIsMockData(true);
        // setError(null);
      }
    } catch (err) {
      datadogLogs.logger.error('Error fetching student interaction data', {
        label: 'api-failure',
        api: 'useStudentInteraction',
        classId
      }, err as Error);
      
      setError(err as Error);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, [classId, dispatchStudentParticipationUpdate]);

  // Initial fetch
  useEffect(() => {
    fetchInteractionData();
  }, [fetchInteractionData]);

  // Set up polling
  useEffect(() => {
    if (pollingInterval <= 0) return;    
    const intervalId = setInterval(() => {
      fetchInteractionData();
    }, pollingInterval);

    // Clean up function to clear the interval when the component unmounts
    return () => {
      clearInterval(intervalId);
    };
  }, [fetchInteractionData, pollingInterval]);

  return {
    interactionData,
    isLoading,
    error,
    refetch: fetchInteractionData,
    isMockData
  };
};

export default useStudentInteraction;